import { extendObservable } from 'mobx'
import moment from 'moment'
import get from 'lodash/get'
import find from 'lodash/find'

const fee = {
  id: null,
  type: null,
  expert: null,
  projectId: null,
  employeeId: null,
  contractId: null,
  eplId: null,
  employees: [],
  deliverableAttach: null,
  deliverableType: null,
  deliverableId: null,
  deliveredBy: null,
  duration: null,
  feeValue: null,
  feeCurrencyId: null,
  credits: null,
  costValue: null,
  costCurrencyId: null,
  costTypeId: null,
  freeCallCostTypeId: null,
  freeCallFeeId: null,
  deliveryDate: null,
  description: null,
  units: null,
  contractTypeId: null,
  attachments: [],
  entityName: null,
  invoiceId: null,
  invoiceNumber: null,
  isBillableToClient: false,
  feeNote: '',
  costNote: ''
}

class Fee {
  constructor (data, staticData) {
    const { FEE_TYPES } = staticData.CONST
    extendObservable(
      this,
      Object.assign(
        {},
        {
          ...fee,
          feeTypeId: FEE_TYPES.BASE_FEE
        }
      )
    )

    if (data.id && !data.type) {
      this.loadFee(data)
    } else {
      extendObservable(this, data)
    }
  }

  loadFee (data) {
    Object.assign(this, fee)

    this.type =
      (find(data.feeItems, feeItem => !!feeItem.feeTypeId) ? 'FEE' : '') +
      (find(data.feeItems, feeItem => !!feeItem.costTypeId) ? 'COST' : '')

    if (data.expert) {
      this.expert = {
        label: data.expert.fullName,
        value: data.expert.id
      }
    }

    const feeItem = find(data.feeItems, i => i.feeTypeId)

    const costItem = find(data.feeItems, i => i.costTypeId)
    Object.assign(this, {
      id: data.id,
      contractId: data.contractId || '0',
      contract: data.contract,
      projectId: data.projectId,
      eplId: data.expertProjectLinkId,
      invoiceId: data.invoiceId,
      deliverableAttach: 'existing',
      deliverableId: data.deliverableId,
      description: data.description,
      deliverableType: data.deliverable && data.deliverable.deliverableTypeId,
      deliveredBy: data.deliveredBy,
      units: data.units || 0,
      deliveryDate: moment(data.deliveryDate).toDate(),
      duration: data.consultationDuration,
      feeTypeId: get(feeItem, 'feeTypeId', null),
      freeCallCostTypeId: get(data, 'freeCallFee.feeItems[0].costTypeId', null),
      freeCallFeeId: get(data, 'freeCallFeeId', null),
      feeCurrencyId: get(feeItem, 'currencyId', null),
      feeValue: get(feeItem, 'value', null),
      feeItemId: get(feeItem, 'id', null),
      costItemId: get(costItem, 'id', null),
      credits: get(feeItem, 'credits', null),
      costTypeId: get(costItem, 'costTypeId', null),
      costCurrencyId: get(costItem, 'currencyId', null),
      costValue: get(costItem, 'value', null),
      isBillableToClient: get(costItem, 'isBillableToClient', null),
      attachments: data.attachments,
      entityName: get(costItem, 'entityName', null),
      invoiceNumber: get(costItem, 'invoiceNumber', null),
      feeNote: get(feeItem, 'note', ''),
      costNote: get(costItem, 'note', '')
    })
  }

  get payload () {
    const payload = {
      id: this.id,
      contractId: (this.contractId !== '0' && this.contractId) || null,
      expertId: get(this, 'expert.value', null),
      projectId: get(this, 'projectId', null),
      expertProjectLinkId: get(this, 'eplId', null),
      deliverableAttach: get(this, 'deliverableAttach', null),
      // TODO: Check if deliverableId is for existing deliverable or type
      deliverableId: get(this, 'deliverableId', null),
      deliveredBy: get(this, 'deliveredBy', null),
      description: this.description,
      deliverable: {
        deliverableTypeId: get(this, 'deliverableType', null)
      },
      units: get(this, 'units', null),
      deliveryDate: moment(this.deliveryDate).format('YYYY-MM-DD'),
      consultationDuration: get(this, 'duration') || 0,
      attachments: this.attachments.map(a => {
        a.fileToFee = {}
        return a
      }),
      feeItems: [],
      freeCallCostTypeId: get(this, 'freeCallCostTypeId', null),
      freeCallFeeId: get(this, 'freeCallFeeId', null),
    }

    if (this.type === 'FEE' || this.type === 'FEECOST') {
      payload.feeItems.push(this.getFee())
      payload.employees = []
    }

    if (this.type === 'COST' || this.type === 'FEECOST') {
      payload.feeItems.push(this.getCost())
    }

    if (this.type === 'COST') {
      payload.employeeId = this.employeeId
    }

    for (let k of Object.keys(payload)) {
      if (!payload[k] && payload[k] !== 0) delete payload[k]
    }

    return payload
  }

  getCost () {
    const cost = {
      id: this.costItemId,
      value: this.costValue || 0,
      currencyId: this.costCurrencyId,
      costTypeId: this.costTypeId,
      entityName: this.entityName,
      invoiceNumber: this.invoiceNumber,
      isBillableToClient: this.isBillableToClient,
      note: this.costNote
    }

    for (let k of Object.keys(cost)) {
      if (cost[k] === undefined || cost[k] === null) delete cost[k]
    }

    return cost
  }

  getFee () {
    const fee = {
      id: this.feeItemId,
      value: this.feeValue || 0,
      currencyId: this.feeCurrencyId,
      feeTypeId: this.feeTypeId,
      credits: get(this, 'credits', undefined),
      note: this.feeNote
    }

    for (let k of Object.keys(fee)) {
      if (!fee[k] && fee[k] !== 0) delete fee[k]
    }

    return fee
  }
}

export default Fee
